import { Head, usePage } from '@inertiajs/react'
import { useI18n } from './_i18n'

// Serialize a JSON-LD object for embedding in a <script>. Neutralize "<" so a
// stray "</script>" (or any "<") in the data can't break out of the tag —
// Inertia's <Head> emits script children verbatim (unescaped).
function ldJson(obj) {
  return JSON.stringify(obj).replace(/</g, '\\u003c')
}

// Shared head/meta component. Renders title, description, canonical, Open Graph
// + Twitter tags, optional robots noindex, and optional JSON-LD into Inertia's
// <Head> (works in SSR and across client navigations). Absolute URLs come from
// the server-shared `canonical_url` / `site_url` props (see ApplicationController
// inertia_share). `head-key` keeps each tag unique so navigations replace rather
// than duplicate them.
export default function Seo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  const {
    site_url: siteUrl = '',
    canonical_url: sharedCanonical = '',
    alternate_urls: alternates = {},
  } = usePage().props
  const url = canonical || sharedCanonical || siteUrl
  const ogImage = image || (siteUrl ? `${siteUrl}/apple-touch-icon.png` : undefined)
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  // Localized product name — the same store-listing brand in every surface:
  // browser-tab title suffix, topnav wordmark, and og:site_name ("물때 타이드",
  // "Mareas cercanas", …). Pages pass their own localized title; the home page
  // passes the brand itself, so it stays unsuffixed. Google's SERP "site name"
  // line is read only from the bare-domain (English) homepage — subdirectory
  // locales can't override it — so the localized og:site_name mainly serves
  // social/messenger link previews; the homepage WebSite JSON-LD declares the
  // localized brands as alternateName (see home/index.jsx).
  const { t } = useI18n()
  const brand = t('app_name')
  const docTitle = title && title !== brand ? `${title} · ${brand}` : brand

  return (
    <Head title={docTitle}>
      {description && <meta name="description" content={description} head-key="description" />}
      <link rel="canonical" href={url} head-key="canonical" />
      {noindex && <meta name="robots" content="noindex" head-key="robots" />}

      {/* hreflang alternates for all 12 languages + x-default (server-shared).
          Lets Google index and serve the right language per URL. */}
      {Object.entries(alternates).map(([tag, href]) => (
        <link key={tag} rel="alternate" hrefLang={tag} href={href} head-key={`alt-${tag}`} />
      ))}

      <meta property="og:site_name" content={brand} head-key="og:site_name" />
      <meta property="og:type" content={type} head-key="og:type" />
      <meta property="og:url" content={url} head-key="og:url" />
      {title && <meta property="og:title" content={title} head-key="og:title" />}
      {description && (
        <meta property="og:description" content={description} head-key="og:description" />
      )}
      {ogImage && <meta property="og:image" content={ogImage} head-key="og:image" />}

      <meta name="twitter:card" content="summary_large_image" head-key="twitter:card" />
      {title && <meta name="twitter:title" content={title} head-key="twitter:title" />}
      {description && (
        <meta name="twitter:description" content={description} head-key="twitter:description" />
      )}
      {ogImage && <meta name="twitter:image" content={ogImage} head-key="twitter:image" />}

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json" head-key={`ld-${i}`}>
          {ldJson(block)}
        </script>
      ))}
    </Head>
  )
}
